import React, { createContext, useContext, useState, ReactNode } from 'react';
import { StepperContextType, Steps } from './stepper-models';

const StepperContext = createContext<StepperContextType | undefined>(undefined);

export const useStepper = () => {
  const context = useContext(StepperContext);
  if (!context) throw new Error('useStepper must be used within a StepperProvider');
  return context;
};

type StepperProviderProps = {
  children: ReactNode;
  steps: Steps;
  isStepValid?: (step: number) => boolean;
  initialStep?: number;
};

const StepperProvider = ({
  children,
  steps,
  isStepValid = () => true,
  initialStep = 1,
}: StepperProviderProps) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<number[]>(
    Array.from({ length: initialStep - 1 }, (_, index) => index + 1)
  );
  const totalSteps = steps.length;

  const nextStep = () => {
    if (currentStep >= totalSteps) return;
    setCompletedSteps((prev) => (prev.includes(currentStep) ? prev : [...prev, currentStep]));
    setCurrentStep((prev) => prev + 1);
  };

  const previousStep = () => {
    if (currentStep <= 1) return;
    setCurrentStep((prev) => prev - 1);
  };

  const goToStep = (step: number) => {
    if (step < 1 || step > totalSteps || !isStepValid(step)) return;
    setCurrentStep(step);
    setCompletedSteps(Array.from({ length: step - 1 }, (_, index) => index + 1));
  };

  return (
    <StepperContext.Provider
      value={{
        currentStep,
        nextStep,
        previousStep,
        goToStep,
        completedSteps,
        setCompletedSteps,
        totalSteps,
        getSteps: () => steps,
      }}
    >
      {children}
    </StepperContext.Provider>
  );
};

export default StepperProvider;
